import { useApolloClient } from '@apollo/client';
import { graphql } from '@orthly/graphql-inline-react';
import { useFeatureFlag } from '@orthly/veneer';
import { atom, useSetAtom } from 'jotai';
import React from 'react';

export const zendeskScheduleAtom = atom([] as any[]);
export const zendeskHolidaysAtom = atom([] as any[]);

export const CheckCsSchedule_Query = graphql(`
    query CheckCsSchedule($args: CheckCsScheduleArgs!) {
        checkCsSchedule(args: $args) {
            is_cs_available
            holidays {
                end_date
                id
                name
                start_date
            }
            schedule {
                end_time
                start_time
            }
            is_holiday
        }
    }
`);

export const useCheckCsSchedule = () => {
    const { value: enableZdScheduler } = useFeatureFlag('enableZdScheduler');
    const client = useApolloClient();
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<Error | null>(null);
    const setZendeskScheduleAtom = useSetAtom(zendeskScheduleAtom);
    const setZendeskHolidaysAtom = useSetAtom(zendeskHolidaysAtom);

    const checkSchedule = React.useCallback(async () => {
        if (enableZdScheduler) {
            setLoading(true);
            /**
             * This _should_ be a lazy query, but we're encountering a bug where every network request gets cancelled instead.
             * This IS resolved newer versions of Apollo Client, but we're not able to upgrade until we fix useChangeSubmissionFn
             * Ticket to fix useChangeSubmissionFn: https://meetdandy.atlassian.net/browse/EPDPLT-4244
             * Related official apollo bug: https://github.com/apollographql/apollo-client/issues/6133
             */
            const { data, error } = await client.query({
                query: CheckCsSchedule_Query,
                variables: { args: { date: null, schedule_id: null } },
                fetchPolicy: 'network-only',
            });
            if (error) {
                setError(error);
            }
            setLoading(false);
            setZendeskScheduleAtom(data?.checkCsSchedule?.schedule ?? []);
            setZendeskHolidaysAtom(data?.checkCsSchedule?.holidays ?? []);
            return data.checkCsSchedule.is_cs_available;
        }
    }, [client, enableZdScheduler, setZendeskScheduleAtom, setZendeskHolidaysAtom]);

    return {
        loading,
        error: error?.message,
        checkSchedule,
    };
};
